// Admin portfolio-composer drafts — SERVER-stored (PUT/GET/DELETE
// /api/v1/admin/portfolio-drafts) so a half-built wizard follows the
// admin across browsers and machines:
//   * /admin/portfolios/new           → clean slate
//   * /admin/portfolios/new?draft=ID  → resumes that draft
//   * the portfolios list page shows all drafts with open/delete
// The wizard snapshot travels as an opaque JSONB payload; `id` is
// client-minted on first save and upserted thereafter.
//
// Drafts originally lived in localStorage; the peek/clear legacy
// helpers below let the portfolios page migrate any local drafts to the
// server once (clearing local copies only after the upload succeeds).

import { attachRequestIdToScope } from "../../../lib/api-client";
import {
  EMPTY_RELEASE_FORM_STATE,
  type DropReleaseFormState,
} from "../drops/DropReleaseForm";

const LEGACY_KEYS = [
  "gh-admin-portfolio-composer-drafts", // multi-draft list era
  "gh-admin-portfolio-composer-draft", // single-slot era
];

const API_BASE = "/api/v1/admin/portfolio-drafts";

export type DraftKind = "regular" | "special";

/** Per-creator draft row. Hello text is ALL that is per-creator now:
 *  tier probabilities moved up to the dossier (see ComposerDraft below),
 *  and share_weight was retired before that. Both removals are load
 *  bearing — see normalizeRows(). */
export type DraftCreatorRow = {
  optional_drop_specific_hello_text: string;
};

export type DraftTierDistribution = {
  common: number;
  preferred: number;
  premium: number;
  founders: number;
};

export type ComposerDraft = {
  id: string;
  saved_at: string; // server updated_at on reads; client stamp on writes
  step: 1 | 2 | 3 | 4 | 5;
  title: string;
  theme: string;
  description: string;
  kind: DraftKind;
  targetCount: string;
  /** Wax-seal base color, lowercase `#rrggbb` (migration 0079). null =
   *  default bordeaux. OPTIONAL so pre-0079 drafts hydrate cleanly. */
  sealColor?: string | null;
  /** Open-roster recruiting mode (zero-creator publish allowed). */
  recruiting: boolean;
  selectedIds: string[];
  rows: Record<string, DraftCreatorRow>;
  /** Tier probabilities are a property of the DOSSIER, not of any creator
   *  in it: one set of odds, applied identically no matter which creator
   *  the draw returns. False means "leave it to the platform default",
   *  which is persisted as portfolios.tier_distribution = NULL. */
  tierOverrideEnabled: boolean;
  tierDistribution: DraftTierDistribution;
  releaseFormState: DropReleaseFormState;
};

// Mirrors system_config.tier_distribution_defaults (migration 0051
// rebalance: premium + founders = 5% exactly) and the composer's
// DEFAULT_TIER_DISTRIBUTION. Duplicated rather than imported because the
// composer page imports this module — importing back would close a cycle.
const DEFAULT_TIER_DISTRIBUTION: DraftTierDistribution = {
  common: 0.75,
  preferred: 0.2,
  premium: 0.049,
  founders: 0.001,
};

function num(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

/** Rebuild each per-creator row from KNOWN KEYS ONLY.
 *
 *  Drafts are stored server-side as opaque JSONB (migration 0052) that
 *  the backend never interprets, so a draft saved before a field was
 *  retired still carries the retired key. TS types erase at runtime, so
 *  deleting the field from DraftCreatorRow does not delete the DATA:
 *  without this rebuild, `rows` was adopted wholesale by reference and
 *  saveDraft() would re-serialize the stale value straight back into the
 *  draft payload on the admin's next save, indefinitely. Rebuilding from
 *  known keys structurally drops the retired key on the first read, so
 *  the next save writes a clean payload.
 *
 *  TWO retirements ride on this rebuild now:
 *    * share_weight — the per-creator ROSTER-draw weight (counsel ruling
 *      D3: equal odds, every creator equally likely).
 *    * override_enabled + tier_overrides — the per-creator TIER-odds
 *      knob. Tier probabilities are per-DOSSIER; a draft could otherwise
 *      keep quietly round-tripping four numbers per creator, and the day
 *      someone reads them back the platform expresses per-creator odds
 *      again. The composer's dossier-level equivalents live on
 *      ComposerDraft (tierOverrideEnabled + tierDistribution), NOT here.
 *
 *  Neither knob survives anywhere, including at rest. */
function normalizeRows(raw: unknown): Record<string, DraftCreatorRow> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, DraftCreatorRow> = {};
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!value || typeof value !== "object") continue;
    const r = value as Record<string, unknown>;
    out[id] = {
      optional_drop_specific_hello_text:
        typeof r.optional_drop_specific_hello_text === "string"
          ? r.optional_drop_specific_hello_text
          : "",
    };
  }
  return out;
}

/** Rebuild the DOSSIER-level tier distribution from known keys, falling
 *  back to the platform defaults per key so a half-written draft can't
 *  produce NaN sliders. Deliberately does NOT look at the per-creator
 *  rows: a legacy draft's per-creator tier_overrides are dropped, not
 *  promoted. Promoting them would guess which creator's numbers spoke
 *  for the dossier, and the draft was never a statement about the
 *  dossier — an unticked box means "platform default", which is the
 *  honest reading of a draft that predates the dossier-level control. */
function normalizeTierDistribution(raw: unknown): DraftTierDistribution {
  const t =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    common: num(t.common, DEFAULT_TIER_DISTRIBUTION.common),
    preferred: num(t.preferred, DEFAULT_TIER_DISTRIBUTION.preferred),
    premium: num(t.premium, DEFAULT_TIER_DISTRIBUTION.premium),
    founders: num(t.founders, DEFAULT_TIER_DISTRIBUTION.founders),
  };
}

/** Coerce unknown JSON into a valid draft, or null. Old drafts carrying
 *  the retired "welcome" kind fold to regular; old drafts carrying a
 *  retired per-creator share_weight or tier_overrides have them stripped
 *  by normalizeRows. Hand-rolled on purpose — there is no zod schema
 *  here, so nothing throws on an unknown key; the risk was silent
 *  survival, not rejection. */
function sanitize(raw: unknown): ComposerDraft | null {
  if (!raw || typeof raw !== "object") return null;
  const d = raw as Partial<ComposerDraft>;
  if (typeof d.id !== "string" || typeof d.title !== "string") return null;
  return {
    id: d.id,
    saved_at: typeof d.saved_at === "string" ? d.saved_at : "",
    step: ([1, 2, 3, 4, 5] as const).includes(d.step as ComposerDraft["step"])
      ? (d.step as ComposerDraft["step"])
      : 1,
    title: d.title,
    theme: typeof d.theme === "string" ? d.theme : "",
    description: typeof d.description === "string" ? d.description : "",
    kind: d.kind === "special" ? "special" : "regular",
    targetCount: typeof d.targetCount === "string" ? d.targetCount : "",
    recruiting: d.recruiting === true,
    selectedIds: Array.isArray(d.selectedIds)
      ? d.selectedIds.filter((x): x is string => typeof x === "string")
      : [],
    rows: normalizeRows(d.rows),
    tierOverrideEnabled: d.tierOverrideEnabled === true,
    tierDistribution: normalizeTierDistribution(d.tierDistribution),
    releaseFormState: {
      ...EMPTY_RELEASE_FORM_STATE,
      ...(d.releaseFormState ?? {}),
    },
  };
}

type ServerDraftRow = {
  id: string;
  title: string;
  payload: unknown;
  updated_at: string;
};

function fromServerRow(row: ServerDraftRow): ComposerDraft | null {
  const payload =
    row.payload && typeof row.payload === "object"
      ? (row.payload as Record<string, unknown>)
      : {};
  return sanitize({ ...payload, id: row.id, saved_at: row.updated_at });
}

async function throwOnError(response: Response, what: string): Promise<never> {
  let message = `${what} failed (HTTP ${response.status})`;
  try {
    const raw = await response.json();
    if (raw?.error?.message) message = raw.error.message;
  } catch {
    // body not JSON
  }
  throw new Error(message);
}

/** All drafts, most recently saved first (server-ordered). */
export async function listComposerDrafts(): Promise<ComposerDraft[]> {
  const response = await fetch(API_BASE, { credentials: "include" });
  attachRequestIdToScope(response);
  if (!response.ok) await throwOnError(response, "Draft list fetch");
  const raw = (await response.json()) as { data: ServerDraftRow[] };
  return raw.data
    .map(fromServerRow)
    .filter((d): d is ComposerDraft => d !== null);
}

/** One draft by id; null when it doesn't exist (deleted elsewhere). */
export async function getComposerDraft(
  id: string,
): Promise<ComposerDraft | null> {
  const response = await fetch(`${API_BASE}/${encodeURIComponent(id)}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (response.status === 404) return null;
  if (!response.ok) await throwOnError(response, "Draft fetch");
  const raw = (await response.json()) as { data: ServerDraftRow };
  return fromServerRow(raw.data);
}

/** Insert or replace by the draft's own id. */
export async function upsertComposerDraft(draft: ComposerDraft): Promise<void> {
  const response = await fetch(`${API_BASE}/${encodeURIComponent(draft.id)}`, {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ title: draft.title, payload: draft }),
  });
  attachRequestIdToScope(response);
  if (!response.ok) await throwOnError(response, "Draft save");
}

/** Idempotent delete (already-gone drafts succeed silently). */
export async function deleteComposerDraft(id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok && response.status !== 404) {
    await throwOnError(response, "Draft delete");
  }
}

// ---- One-time localStorage → server migration --------------------------

/** Read (without clearing) any drafts left over from the localStorage
 *  era. The caller uploads them and then calls clearLegacyLocalDrafts()
 *  — clearing only after a successful upload so a dead backend can't
 *  eat drafts. */
export function peekLegacyLocalDrafts(): ComposerDraft[] {
  const found: ComposerDraft[] = [];
  for (const key of LEGACY_KEYS) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const items = Array.isArray(parsed) ? parsed : [parsed];
      for (const item of items) {
        const d = sanitize(
          item && typeof item === "object" && !("id" in (item as object))
            ? { ...(item as object), id: crypto.randomUUID() }
            : item,
        );
        if (d) {
          if (!d.saved_at) d.saved_at = new Date().toISOString();
          found.push(d);
        }
      }
    } catch {
      // unreadable legacy slot — skip
    }
  }
  return found;
}

export function clearLegacyLocalDrafts(): void {
  for (const key of LEGACY_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // storage unavailable
    }
  }
}
